import { useCallback } from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../api/graphql'
import { BILLING_PORTAL_MUTATION, BillingPortalResponse } from './BusinessCalendarQueries'

type BillingPortalPayload = BillingPortalResponse['businessSubscriptionBillingPortal'] | undefined

/**
 * Pure handler for a billing-portal mutation result. Extracted from the hook so
 * the branch logic (errors -> surface message, no redirect; portalUrl ->
 * redirect) is unit-testable without React/Apollo. The side effects are
 * injected so tests can assert on them.
 */
export function handleBillingPortalResult(
  response: BillingPortalPayload,
  { onError, onRedirect }: { onError: (message: string) => void; onRedirect: (url: string) => void }
): void {
  if (response?.errors && response.errors.length > 0) {
    onError(response.errors[0].message)
    return
  }

  if (response?.portalUrl) {
    onRedirect(response.portalUrl)
  }
}

/**
 * Shared handler for opening the Stripe billing portal. Both the Billing tab's
 * "Manage Billing" button and the past-due recovery banner use this so there's
 * a single source of truth for the portal flow (mutation -> redirect to portalUrl).
 */
export function useBillingPortal(organizationId: string): { openPortal: () => Promise<void>; loading: boolean } {
  const [runMutation, { loading }] = useMutation<BillingPortalResponse>(BILLING_PORTAL_MUTATION)

  const openPortal = useCallback(async () => {
    const result = await runMutation({ variables: { organizationId } })

    handleBillingPortalResult(result.data?.businessSubscriptionBillingPortal, {
      onError: message => toastr.error(message),
      onRedirect: url => {
        window.location.href = url
      },
    })
  }, [runMutation, organizationId])

  return { openPortal, loading }
}
